import React, { useMemo, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

/**
 * BlogIndex.js
 * CashifyGCMart — Blog landing with search, filters, sort, and responsive cards.
 * Merges static POSTS with AI-generated articles from the API.
 */

const POSTS = [
  // Core guides (3)
  {
    slug: "how-to-sell-gift-cards-safely-2025",
    title: "How to Sell Gift Cards Online Safely in 2025",
    excerpt: "The definitive step-by-step guide to selling gift cards securely—avoid scams, verify balances, and maximize payouts.",
    date: "2025-10-28",
    minutes: 9,
    category: "Guides",
    image: "/images/blog/safe-selling-2025.webp",
    keywords: ["safety", "verification", "how to sell"],
  },
  {
    slug: "amazon-gift-card-exchange-guide",
    title: "Amazon Gift Card Exchange: Complete Guide",
    excerpt: "Everything you need to know to turn Amazon credits into instant cash—rates, verification, and common pitfalls.",
    date: "2025-10-28",
    minutes: 8,
    category: "Guides",
    image: "/images/blog/amazon-guide.webp",
    keywords: ["amazon", "exchange", "rates"],
  },
  {
    slug: "best-instant-payout-methods",
    title: "Best Instant Payout Methods for Gift Card Sales",
    excerpt: "Compare instant payout options and choose the fastest, most secure method for your card sales.",
    date: "2025-10-28",
    minutes: 7,
    category: "Payments",
    image: "/images/blog/instant-payouts.webp",
    keywords: ["payouts", "zelle", "paypal"],
  },
  // Brand guides (19)
  {
    slug: "apple-gift-cards-resale-value",
    title: "Apple Gift Cards: Why They Hold Strong Resale Value",
    excerpt: "Premium demand and flexible use keep Apple cards near the top of the resale market—here's how to get the best rate.",
    date: "2025-10-29",
    minutes: 8,
    category: "Brand Guides",
    image: "/images/blog/apple.webp",
    keywords: ["apple", "itunes", "rates"],
  },
  {
    slug: "walmart-gift-cards-cashout-tips",
    title: "Walmart Gift Cards: Instant Cash-Out Tips & Buyer Insights",
    excerpt: "Verification tips, typical payout ranges, and how to speed up same-day payments for Walmart cards.",
    date: "2025-10-29",
    minutes: 8,
    category: "Brand Guides",
    image: "/images/blog/walmart.webp",
    keywords: ["walmart", "cash out", "verify"],
  },
  {
    slug: "target-gift-cards-selling-guide",
    title: "Target Gift Cards: How to Sell & Avoid Common Pitfalls",
    excerpt: "Avoid the mistakes that slow payouts—best practices for Target card submissions and documentation.",
    date: "2025-10-29",
    minutes: 9,
    category: "Brand Guides",
    image: "/images/blog/target.webp",
    keywords: ["target", "selling", "tips"],
  },
  {
    slug: "best-buy-gift-cards-trading-guide",
    title: "Best Buy Gift Cards: Safe Online Trading Guide",
    excerpt: "Electronics cards move fast—learn rate bands, ideal timing, and receipt tips for Best Buy cards.",
    date: "2025-10-29",
    minutes: 9,
    category: "Brand Guides",
    image: "/images/blog/bestbuy.webp",
    keywords: ["best buy", "electronics", "verification"],
  },
  {
    slug: "home-depot-gift-cards-verification",
    title: "Home Depot Gift Cards: A Seller's Fast Verification Guide",
    excerpt: "High-value home improvement cards: how to prepare photos and pass manual checks quickly.",
    date: "2025-10-29",
    minutes: 8,
    category: "Brand Guides",
    image: "/images/blog/homedepot.webp",
    keywords: ["home depot", "verification", "payout"],
  },
  {
    slug: "lowes-gift-cards-rates-guide",
    title: "Lowe's Gift Cards: Compare Rates & Sell Securely",
    excerpt: "Typical payout percentages, bulk tips, and documentation that unlocks faster approvals.",
    date: "2025-10-29",
    minutes: 7,
    category: "Brand Guides",
    image: "/images/blog/lowes.webp",
    keywords: ["lowe's", "rates", "secure"],
  },
  {
    slug: "macys-gift-cards-selling-guide",
    title: "Macy's Gift Cards: Fashion Value You Can Redeem",
    excerpt: "Fashion cards that hold strong value—how to submit Macy's cards for smooth same-day payouts.",
    date: "2025-10-29",
    minutes: 7,
    category: "Brand Guides",
    image: "/images/blog/macys.webp",
    keywords: ["macy's", "fashion", "resale"],
  },
  {
    slug: "lululemon-gift-cards-resale-demand",
    title: "Lululemon Gift Cards: Hot in Resale Markets",
    excerpt: "Why athleisure demand keeps these cards liquid—and how to get premium rates.",
    date: "2025-10-29",
    minutes: 8,
    category: "Brand Guides",
    image: "/images/blog/lululemon.webp",
    keywords: ["lululemon", "athletic", "demand"],
  },
  {
    slug: "dicks-sporting-goods-gift-cards",
    title: "Dick's Sporting Goods Gift Cards: Turn Gear into Cash",
    excerpt: "Sell sports store credits quickly—evidence to include and timing for top demand.",
    date: "2025-10-29",
    minutes: 8,
    category: "Brand Guides",
    image: "/images/blog/dicks.webp",
    keywords: ["sporting goods", "dick's", "gear"],
  },
  {
    slug: "sephora-gift-cards-beauty-cash",
    title: "Sephora Gift Cards: Beauty Credits to Cash",
    excerpt: "Beauty cards with loyal buyers—optimize images and receipts to speed up validation.",
    date: "2025-10-29",
    minutes: 7,
    category: "Brand Guides",
    image: "/images/blog/sephora.webp",
    keywords: ["sephora", "beauty", "cash"],
  },
  {
    slug: "starbucks-gift-cards-instant-cash",
    title: "Starbucks Gift Cards: Coffee Credits to Instant Cash",
    excerpt: "Everyday demand = fast sales. Make your Starbucks submission approval-ready.",
    date: "2025-10-29",
    minutes: 6,
    category: "Brand Guides",
    image: "/images/blog/starbucks.webp",
    keywords: ["starbucks", "coffee", "instant cash"],
  },
  {
    slug: "disney-gift-cards-trading-guide",
    title: "Disney Gift Cards: Magic to Money Trading Guide",
    excerpt: "Seasonality, receipts, and family travel demand—how to time your Disney card sale.",
    date: "2025-10-29",
    minutes: 8,
    category: "Brand Guides",
    image: "/images/blog/disney.webp",
    keywords: ["disney", "parks", "trading"],
  },
  {
    slug: "nike-gift-cards-resale-options",
    title: "Nike Gift Cards: Fast Resale Options for Sports Lovers",
    excerpt: "Leverage product drops and sports seasons to secure premium payouts on Nike cards.",
    date: "2025-10-29",
    minutes: 8,
    category: "Brand Guides",
    image: "/images/blog/nike.webp",
    keywords: ["nike", "sneakers", "sports"],
  },
  {
    slug: "ebay-gift-cards-seller-guidelines",
    title: "eBay Gift Cards: Seller Guidelines & Instant Trade",
    excerpt: "Marketplace cards with massive buyer bases—what to include for frictionless approvals.",
    date: "2025-10-29",
    minutes: 7,
    category: "Brand Guides",
    image: "/images/blog/ebay.webp",
    keywords: ["ebay", "marketplace", "instant"],
  },
  {
    slug: "steam-gift-cards-gamer-cashouts",
    title: "Steam Gift Cards: Fast Cashouts for Gamers",
    excerpt: "Tap into sale seasons and provide clean screenshots for lightning-fast payouts.",
    date: "2025-10-29",
    minutes: 7,
    category: "Brand Guides",
    image: "/images/blog/steam.webp",
    keywords: ["steam", "gaming", "wallet"],
  },
  {
    slug: "razer-gold-gift-cards-gaming-credits",
    title: "Razer Gold Gift Cards: Sell Gaming Credits for Cash",
    excerpt: "Universal gaming currency with global demand—maximize value with proof of purchase.",
    date: "2025-10-29",
    minutes: 7,
    category: "Brand Guides",
    image: "/images/blog/razer.webp",
    keywords: ["razer gold", "gaming", "credits"],
  },
  {
    slug: "ulta-beauty-gift-cards-cash-options",
    title: "Ulta Beauty Gift Cards: Quick Cash Options",
    excerpt: "Beauty seasons and insider tips to push your Ulta payout to the top of the range.",
    date: "2025-10-29",
    minutes: 7,
    category: "Brand Guides",
    image: "/images/blog/ulta.webp",
    keywords: ["ulta", "beauty", "cash"],
  },
  {
    slug: "amex-gift-cards-secure-resale",
    title: "American Express Gift Cards: Secure Resale Guide",
    excerpt: "Universal acceptance means strong resale—how to prepare Amex card details securely.",
    date: "2025-10-29",
    minutes: 9,
    category: "Brand Guides",
    image: "/images/blog/amex.webp",
    keywords: ["amex", "prepaid", "secure"],
  },
  {
    slug: "visa-gift-cards-instant-conversion",
    title: "Visa Gift Cards: Convert to Cash Instantly & Safely",
    excerpt: "Bank-issued prepaid cards with premium liquidity—what reviewers check before payout.",
    date: "2025-10-29",
    minutes: 9,
    category: "Brand Guides",
    image: "/images/blog/visa.webp",
    keywords: ["visa", "prepaid", "convert"],
  },
  {
    slug: "sell-gift-cards-safely-complete-guide",
    title: "How to Sell Gift Cards Online Safely: Complete 2024 Security Guide",
    excerpt: "Master safe gift card selling with our comprehensive security guide. Learn platform selection, fraud prevention, and secure payment methods.",
    date: "2025-11-26",
    publishDate: "2025-11-26", // Already published
    minutes: 8,
    category: "Security",
    image: "/images/blog/safe-selling-2025.webp",
    keywords: ["safety", "security", "best practices", "trusted platforms"],
  },
  {
    slug: "best-platforms-sell-unwanted-gift-cards",
    title: "Where to Sell Unwanted Gift Cards: Top 5 Platforms Compared 2025",
    excerpt: "Comprehensive comparison of the top 5 gift card selling platforms. Find the best rates, fastest payouts, and most secure options for 2024.",
    date: "2025-11-29",
    publishDate: "2025-11-29", // Publishes in 3 days
    minutes: 9,
    category: "Guides",
    image: "/images/blog/platform-comparison-2025.webp",
    keywords: ["comparison", "rates", "reviews", "platforms"],
  },
  {
    slug: "best-gift-card-selling-rates-comparison",
    title: "Best Gift Card Selling Rates: 2024 Complete Comparison Guide",
    excerpt: "Discover which platforms offer the best gift card selling rates. Compare payouts, learn how to maximize earnings, and understand rate calculations.",
    date: "2025-12-02",
    publishDate: "2025-12-02", // Publishes in 6 days
    minutes: 10,
    category: "Guides",
    image: "/images/blog/rates-comparison-2025.webp",
    keywords: ["rates", "comparison", "maximize payout"],
  },
  {
    slug: "sell-gift-cards-instantly-same-day",
    title: "How to Sell Gift Cards Instantly: Get Cash in 6-14 Hours",
    excerpt: "Need cash fast? Learn how to sell gift cards instantly with same-day payouts. Complete guide to 6-14 hour processing and instant payment methods.",
    date: "2025-12-05",
    publishDate: "2025-12-05", // Publishes in 9 days
    minutes: 7,
    category: "Guides",
    image: "/images/blog/instant-cash-2025.webp",
    keywords: ["instant", "fast payout", "same-day"],
  },
  {
    slug: "sell-partial-balance-gift-cards-guide",
    title: "Can I Sell Partial Balance Gift Cards? Complete Guide 2025",
    excerpt: "Yes! Learn how to sell gift cards with partial balances. Discover which platforms accept them and how welcome bonuses can exceed card value.",
    date: "2025-12-08",
    publishDate: "2025-12-08", // Publishes in 12 days
    minutes: 6,
    category: "Guides",
    image: "/images/blog/partial-balance-2025.webp",
    keywords: ["partial balance", "leftover value", "tips"],
  },
  {
    slug: "sell-gift-cards-paypal-instant-cash",
    title: "Where to Sell Gift Cards for PayPal Cash: Best Platforms 2025",
    excerpt: "Discover the best platforms for selling gift cards and receiving instant PayPal cash. Compare speeds, rates, and get paid in 6-14 hours.",
    date: "2025-12-11",
    publishDate: "2025-12-11", // Publishes in 15 days
    minutes: 6,
    category: "Payments",
    image: "/images/blog/paypal-cash-2025.webp",
    keywords: ["PayPal", "payment options", "instant"],
  },
];

const CATEGORIES = ["Guides", "Brand Guides", "Payments", "Security"];

const SORTS = [
  { id: "newest", label: "Newest" },
  { id: "oldest", label: "Oldest" },
  { id: "title", label: "Title A–Z" },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function FilterPill({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={classNames(
        "whitespace-nowrap rounded-lg px-3 py-2 text-sm transition",
        active
          ? "bg-fuchsia-500 text-white shadow-sm"
          : "text-neutral-300 hover:bg-white/5"
      )}
      aria-pressed={active}
    >
      {label}
    </button>
  );
}

const BlogIndex = () => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState("newest");
  const [apiArticles, setApiArticles] = useState([]);

  // Fetch AI-generated articles from the API
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const res = await fetch(`${backendUrl}/api/blog/articles?limit=100`);
        if (res.ok) {
          const data = await res.json();
          // Convert API format to match POSTS format
          const mapped = (data.articles || []).map(a => ({
            slug: a.slug,
            title: a.title,
            excerpt: a.excerpt || '',
            date: a.publish_date ? a.publish_date.split('T')[0] : new Date().toISOString().split('T')[0],
            minutes: a.read_time || 8,
            category: a.category || 'Guides',
            image: a.image_url || '/images/blog/default-blog.webp',
            keywords: a.keywords || [],
            isAiGenerated: true,
          }));
          setApiArticles(mapped);
        }
      } catch {
        // API unavailable - only show static posts
      }
    };
    fetchArticles();
  }, []);

  const filtered = useMemo(() => {
    // Merge static POSTS + API articles, deduplicate by slug
    const staticSlugs = new Set(POSTS.map(p => p.slug));
    const merged = [...POSTS, ...apiArticles.filter(a => !staticSlugs.has(a.slug))];

    const now = new Date();
    let list = merged.filter(post => {
      if (post.publishDate) {
        const publishDate = new Date(post.publishDate);
        return publishDate <= now;
      }
      return true;
    });

    // Filter category
    if (category !== "All") {
      list = list.filter((p) => p.category === category);
    }
    // Search query (title, excerpt, keywords)
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const hay = `${p.title} ${p.excerpt} ${(p.keywords || []).join(" ")}`.toLowerCase();
        return hay.includes(q);
      });
    }
    // Sort
    if (sort === "newest") {
      list.sort((a, b) => (a.date < b.date ? 1 : -1));
    } else if (sort === "oldest") {
      list.sort((a, b) => (a.date > b.date ? 1 : -1));
    } else {
      list.sort((a, b) => a.title.localeCompare(b.title));
    }
    return list;
  }, [query, category, sort, apiArticles]);

  // Old placeholder data for reference (removed)
  const blogPosts = [
    {
    slug: "sell-vs-trade-gift-cards",
    title: "Is It Better to Sell or Trade Gift Cards? Complete Comparison 2025",
    excerpt: "Wondering whether to sell or trade your gift cards? Compare the pros and cons of each option to maximize value.",
    date: "2025-12-14",
    publishDate: "2025-12-14", // Publishes in 18 days
    minutes: 8,
    category: "Guides",
    image: "/images/blog/sell-vs-trade-2025.webp",
    keywords: ["sell vs trade", "comparison", "best option"],
  },
  {
    slug: "apps-sell-gift-cards-mobile",
    title: "What Apps Allow Selling Gift Cards from My Phone? Top 5 Mobile Solutions 2025",
    excerpt: "Discover the best mobile apps for selling gift cards directly from your phone. Compare features, payout speeds, and rates.",
    date: "2025-12-17",
    publishDate: "2025-12-17", // Publishes in 21 days
    minutes: 8,
    category: "Guides",
    image: "/images/blog/mobile-apps-2025.webp",
    keywords: ["mobile apps", "phone", "sell on go"],
  },
  {
    slug: "sell-gift-cards-zero-fees",
    title: "How to Sell Gift Cards Without Paying Fees? Zero-Fee Platforms Guide 2025",
    excerpt: "Discover how to sell gift cards without paying any fees. Learn which platforms charge zero fees and maximize your payout.",
    date: "2025-12-20",
    publishDate: "2025-12-20", // Publishes in 24 days
    minutes: 8,
    category: "Guides",
    image: "/images/blog/zero-fees-2025.webp",
    keywords: ["zero fees", "no fees", "maximize payout"],
  },
];

  return (
    <>
      <Helmet>
        <title>Gift Card Selling Tips & Guides | Cashifygcmart Blog</title>
        <meta 
          name="description" 
          content="Expert guides, tips, and insights on selling gift cards online. Learn about rates, security, and maximizing your payouts."
        />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Cashifygcmart Blog",
            "url": "https://cashifygcmart.com/blog",
            "description": "Official blog of Cashifygcmart — your trusted platform to sell unused gift cards instantly for cash. Learn resale values, verification tips, and payout strategies for over 20+ brands.",
            "publisher": {
              "@type": "Organization",
              "name": "Cashifygcmart",
              "url": "https://cashifygcmart.com"
            }
          })}
        </script>
      </Helmet>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10">
        {/* Hero */}
        <section className="relative overflow-hidden rounded-3xl bg-gradient-to-tr from-indigo-600 via-fuchsia-600 to-amber-500 p-[1px]">
          <div className="rounded-3xl bg-neutral-950">
            <div className="relative isolate px-6 py-10 sm:py-12 sm:px-10">
              {/* soft glow */}
              <div className="pointer-events-none absolute -top-24 -left-16 h-40 w-40 rounded-full bg-fuchsia-500/30 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-20 -right-10 h-48 w-48 rounded-full bg-amber-400/25 blur-3xl" />
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white">
                CashifyGCMart Blog
              </h1>
              <p className="mt-2 max-w-2xl text-neutral-300">
                Guides, brand-specific rates, and security tips to help you sell
                unused gift cards with confidence and get paid fast.
              </p>

              {/* Controls */}
              <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {/* Search */}
                <label className="group relative">
                  <span className="sr-only">Search articles</span>
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search brand, topic, or keyword…"
                    className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-neutral-400 outline-none ring-0 transition focus:border-fuchsia-400/50"
                  />
                  <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400">
                    ⌘K
                  </span>
                </label>

                {/* Category */}
                <div className="flex gap-2 overflow-x-auto rounded-xl border border-white/10 bg-white/5 p-2">
                  <FilterPill
                    label="All"
                    active={category === "All"}
                    onClick={() => setCategory("All")}
                  />
                  {CATEGORIES.map((c) => (
                    <FilterPill
                      key={c}
                      label={c}
                      active={category === c}
                      onClick={() => setCategory(c)}
                    />
                  ))}
                </div>

                {/* Sort */}
                <div className="lg:col-span-2 flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2">
                  <div className="text-neutral-300 text-sm">
                    {filtered.length} article{filtered.length === 1 ? "" : "s"}
                  </div>
                  <div className="flex items-center gap-2">
                    {SORTS.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => setSort(s.id)}
                        className={classNames(
                          "rounded-lg px-3 py-2 text-sm transition",
                          sort === s.id
                            ? "bg-white/10 text-white"
                            : "text-neutral-300 hover:bg-white/5"
                        )}
                        aria-pressed={sort === s.id}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Grid */}
        <section className="mt-8">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((post) => (
              <Link
                key={post.slug}
                to={`/blog/${post.slug}`}
                className="group relative overflow-hidden rounded-2xl border border-neutral-800/70 bg-neutral-950 hover:border-neutral-700"
              >
                {/* cover */}
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                    loading="lazy"
                  />
                  {/* animated gradient edge */}
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-neutral-950/90 to-transparent" />
                </div>

                {/* content */}
                <div className="p-4">
                  <div className="flex items-center gap-2 text-xs text-neutral-400">
                    <span className="rounded-full bg-white/5 px-2 py-1 text-[11px] tracking-wide">
                      {post.category}
                    </span>
                    <span>•</span>
                    <time dateTime={post.date}>
                      {new Date(post.date).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </time>
                    <span>•</span>
                    <span>{post.minutes} min read</span>
                  </div>

                  <h3 className="mt-2 line-clamp-2 text-base font-semibold leading-snug text-white">
                    {post.title}
                  </h3>
                  <p className="mt-1 line-clamp-2 text-sm text-neutral-300">
                    {post.excerpt}
                  </p>

                  {/* CTA */}
                  <div className="mt-3 flex items-center gap-2 text-fuchsia-300">
                    <span className="text-sm font-medium">Read article</span>
                    <svg
                      className="h-4 w-4 -translate-x-0.5 opacity-70 transition group-hover:translate-x-0.5 group-hover:opacity-100"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10.293 3.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 11-1.414-1.414L13.586 10H4a1 1 0 110-2h9.586l-3.293-3.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>

                {/* subtle hover ring */}
                <div className="pointer-events-none absolute inset-0 rounded-2xl ring-0 ring-fuchsia-400/0 transition group-hover:ring-2 group-hover:ring-fuchsia-400/30" />
              </Link>
            ))}
          </div>

          {/* Empty state */}
          {filtered.length === 0 && (
            <div className="mt-16 text-center">
              <p className="text-neutral-400">
                No articles match your search. Try different keywords or clear
                filters.
              </p>
            </div>
          )}
        </section>

        {/* SEO helper links */}
        <section className="mt-10 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-neutral-800/60 bg-neutral-950 p-4">
          <div className="text-sm text-neutral-300">
            Want brand-specific rates now?
          </div>
          <div className="flex gap-2">
            <Link
              to="/rate-calculator"
              className="rounded-xl bg-white/10 px-3 py-2 text-sm text-white transition hover:bg-white/20"
            >
              Rate Calculator
            </Link>
            <Link
              to="/form-submission"
              className="rounded-xl bg-fuchsia-500 px-3 py-2 text-sm font-semibold text-white transition hover:bg-fuchsia-500/90"
            >
              Submit Your Card
            </Link>
          </div>
        </section>
      </main>
    </>
  );
};

export default BlogIndex;
